import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionSizes from '../../_Sections/SectionSizes';
import { useSelector } from 'react-redux';
import WebsiteStore from '../../../WebsiteStore';
import { Box, Container, Grid, Stack, Typography, styled } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SectionPricing from '../../_Sections/SectionPricing';
import tKey from '../../../Common/Utils/tKey';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import { BlackCheckedIcon } from '../../../Common/_components/CheckBoxes';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const PRODUCT_ID = 134;
const VIEW_PRODUCT_ID = 163;

const CustomWarning = styled('div')(({ theme }) => ({
  marginTop: 60,
  color: 'white',
  textAlign: 'center',
}))

export default function PlacematPage() {
  const [t] = useTranslation('page/products/vegan-leather-placemats');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

  if (!productData) return null

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom all-over print Vegan Leather Placemats | Products')}
      description={t('Design and create your own custom printed Vegan Leather Placemats featuring your artwork. ✔ Print-on-demand drop shipping ✔ No minimums ✔ Made in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#b5ffd6"
      bgStyle={{
        backgroundPosition: 'center right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '49%',
      }}
      fullPageBg
      tag={t('The all-over print')}
      title={t('Vegan Leather Placemat')}
      text={t('Add a modern touch to your table with custom printed fine grain vegan leather placemats. Printed on-demand, they feature a smooth, wipeable surface and a soft grey sueded underside to protect your table. Vibrant, durable UV inks ensure long-lasting color. Sold solo or in sets of 4—perfect for any table setting.')}
      createUrl={productData.get('url_slug')}
      img={require('@resources/img/pages/products/vegan-leather-placemats/banner-1-' + locale + '.png')}
      alt={t('Picture of Placemat')}
      details={[
        {
          img: require('@resources/img/pages/products/vegan-leather-placemats/icon-1.svg?url'),
          alt: t('Vegan Leather icon'),
          text: t('Made from fine grain vegan leather'),
        },
        {
          img: require('@resources/img/pages/products/vegan-leather-placemats/icon-2.svg?url'),
          alt: t('Heat and moisture icon'),
          text: t('Smooth front surface and sueded underside'),
        },
        {
          img: require('@resources/img/pages/products/vegan-leather-placemats/icon-3.svg?url'),
        alt: t('Rounded corners icon'),
          text: t('Rounded corners with laser cut edges'),
        },
        {
          img: require('@resources/img/pages/products/vegan-leather-placemats/icon-4.svg?url'),
          alt: t('Print icon'),
          text: t('Durable and vibrant UV print on the front and light grey back'),
        },
        {
          img: require('@resources/img/pages/products/vegan-leather-placemats/icon-5.svg?url'),
          alt: t('Canada icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />
    <SectionGallery images={[
      require('@resources/img/pages/products/vegan-leather-placemats/gallery_0.jpg'),
      require('@resources/img/pages/products/vegan-leather-placemats/gallery_1.jpg'),
      require('@resources/img/pages/products/vegan-leather-placemats/gallery_2.jpg'),
      require('@resources/img/pages/products/vegan-leather-placemats/gallery_3.jpg'),
      require('@resources/img/pages/products/vegan-leather-placemats/gallery_4.jpg'),
    ]} />

    <SectionDropship
      title={<Trans
        t={t}
        i18nKey={tKey('<yellow>Drop ship</yellow> your Vegan Leather Placemat<br/> <pink>on-demand</pink>')}
        components={{
          yellow: <span className="ty" />,
          pink: <span className="tp" />,
        }}
      />}
      text={t('Your brand and following will love Vegan Leather Placemat! At prices designed for reselling, Vegan Leather Placemat made on demand meet the needs of sellers who prefer not to stock inventory. Sell them in your eCommerce store and decide your profit margin')}
    />

    <SectionPricing
      productId={PRODUCT_ID}
      listOptions
    />

    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Vegan Leather Placemat Sizing')}
      text={<Trans
        t={t}
        i18nKey={tKey('Approximately <size/>. Sold as individual pieces, or as a set of 4.')}
        components={{
          size: <SizeDisplay sizes={[17.5, 13]} sourceUnit="in" displayType="physical" />,
        }}
      />}
    >
      <img
        src={require('@resources/img/pages/products/vegan-leather-placemats/measurement.svg?url')}
        style={{
          marginRight: 0,
        }}
      />
      <CustomWarning>
        <p>{t('When designing your Vegan Leather Placemats, we recommend avoiding borders around the outside of your design as they may not perfectly align on the finished product. Expect some variance between your design placement in the Design Lab versus the finished printed product.')}</p>
      </CustomWarning>
    </SectionSizes>

    <SectionBranding
      title={t('Brand your Vegan Leather Placemat')}
      text={t('Branding enhances your products. Add custom labels, artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Vegan Leather Placemat with these great products to create your own unique styles.')}
      productSlugs={['cotton-sateen-tablecloth', 'coaster', 'stainless-steel-tumbler']}
    />
  </div>
}
